import { useParams } from "react-router";
import {
  BackgroundTaskStatus,
  BulkDownloadInvoiceTaskStatus,
  getStatementSplitData,
  IStatementLine,
  StatementLineType,
  StatementType,
} from "shared";
import {
  useState,
  useReducer,
  useRef,
  useEffect,
} from "react";
import { toast } from "react-toastify";
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

import { useSession } from "../../hooks/useSession";
import { useFetchContactQuery, useSmsOptOutMutation } from "../../services/api/contact";
import { useApiRequest } from "../../../main/hooks/useApiRequest";
import { env } from "../../../common/lib/env";
import { StripeCheckout } from "../Stripe/StripeCheckout";
import { LoadingFallback } from "../LoadingFallback";
import { classNames } from "../../../common/lib/classNames";
import { formatAmount } from "../../../common/lib/helpers";
import { Toggle } from "../../../common/Atoms/Toggle";
import {
  Banner,
  card,
  primaryPayButton,
  Segmented,
} from "../portalUi";

import { StatementSection } from "./Statement/StatementSection";
import { SavedPaymentMethods } from "./ContactPage";

export function loadingReducer(state: Record<string, boolean>, action: Record<string, boolean>) {
  return {
    ...state,
    ...action,
  };
}

type PayOption = `due` | `overdue` | `all` | `selected`;

const agedColors = [`bg-paidniceGreen-600`, `bg-amber-500`, `bg-paidniceRed-600`, `bg-paidniceRed-900`];

/** Describes which invoices count as "due now" for a split statement */
function dueNowDescription(splitDayIndex: number) {
  const days = Math.abs(splitDayIndex);
  const plural = days === 1 ? `` : `s`;

  if (splitDayIndex < 0) {
    return `Overdue or due within ${days} day${plural}`;
  }

  if (splitDayIndex === 0) {
    return `Due today or earlier`;
  }

  return `${days} or more day${plural} overdue`;
}

function sumOutstanding(lines: IStatementLine[]) {
  return lines.reduce((acc, l) => acc + l.outstandingInCents, 0);
}

function SummaryRow({ dot, label, sub, value, red }: { dot: string; label: string; sub?: string; value: string; red?: boolean }) {
  return (
    <div className={ `flex items-start justify-between gap-2.5` }>
      <span className={ `flex items-start gap-2 text-gray-600` }>
        <span className={ classNames(`mt-1.5 h-2 w-2 shrink-0 rounded-full`, dot) } />
        <span className={ `flex flex-col gap-px` }>
          <span>{ label }</span>
          { sub && <span className={ `text-xs text-gray-500` }>{ sub }</span> }
        </span>
      </span>
      <span className={ red ? `font-semibold text-paidniceRed-700` : `text-gray-900` }>{ value }</span>
    </div>
  );
}

interface StatementsProps {
  type: `outstanding` | `paid`;
}

export function Statements({
  type,
}: StatementsProps) {
  const { code } = useParams();
  const session = useSession();
  const { data: contactResult, isLoading, refetch } = useFetchContactQuery({
    code,
    sessionId: session?.id,
  },
  {
    skip: !code || !session?.id,
    pollingInterval: 2 * 60 * 1000, // Poll every 2 minutes
    skipPollingIfUnfocused: true,
  });
  const [currency, setCurrency] = useState<string>();
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [payPick, setPayPick] = useState<PayOption | null>(null);
  /** Invoices in the open checkout */
  const [invoiceIdsToPay, setInvoiceIdsToPay] = useState<string[] | null>(null);
  const [downloadingInvoices, setDownloadingInvoices] = useReducer(loadingReducer, {});
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<`card` | `dd`>(`card`);
  const [downloadStatementLoading, setDownloadStatementLoading] = useState(false);
  const [downloadBulkLoading, setDownloadBulkLoading] = useState(false);
  const [smsOptOutReq, { isLoading: smsOptOutLoading }] = useSmsOptOutMutation();

  const request = useApiRequest();

  // Track mount state so async handlers don't call RTK Query refetch() after unmount
  // (refetch throws once the query subscription has been released).
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function downloadStatement(currencyCode: string, type: StatementType) {
    try {
      setDownloadStatementLoading(true);

      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.basePortalApiUrl}contact/code/${contactResult.hash}/statement.pdf`,
        params: {
          currency: currencyCode,
          type,
        },
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;

      const fileNamePeriodSection = contactResult.statement.period.end ? `_${contactResult.statement.period.end}` : ``;
      link.setAttribute(`download`, `${contactResult.statement.contact.name}${fileNamePeriodSection}_outstanding.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    }
    catch (e) {
      console.error(e);
      // TODO: how to present error cleanly on portal
    }
    finally {
      setDownloadStatementLoading(false);
      // this may have updated the contact so reload (only if still mounted;
      // the user may have navigated away while the PDF was downloading)
      if (isMountedRef.current) {
        refetch();
      }
    }
  }

  async function onDownloadInvoice(line: IStatementLine) {
    if (line.type !== StatementLineType.Invoice && line.type !== StatementLineType.PaidInvoice) return;

    const invoiceId = line.invoiceId;

    if (!line.hash) {
      throw new Error(`Invoice hash not found`);
    }

    setDownloadingInvoices({
      [invoiceId]: true,
    });

    try {
      const filename = `${line.invoiceNumber}.pdf`;

      const pdfLink = `${env.basePortalApiUrl}invoices/code/${line.hash}/download.pdf`;

      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: pdfLink,
        params: {
          filename,
          mode: `download`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));

      const link = document.createElement(`a`);
      link.href = url;
      link.target = `_blank`;

      link.setAttribute(`download`, filename);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download invoice`}`);
    }

    setDownloadingInvoices({
      [invoiceId]: false,
    });
  }

  function onSmsOptOut() {
    if (!contactResult) return;

    if (!session?.id) return;

    smsOptOutReq({
      code: contactResult.hash,
      smsOptOut: !contactResult.smsOptOut,
      sessionId: session.id,
    });
  }

  async function onDownloadBulkJob(taskId: number) {
    const contactName = contactResult?.statement?.contact?.name;

    if (!contactName) {
      toast.error(`Contact name not found`);

      return;
    }

    setDownloadBulkLoading(true);

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.basePortalApiUrl}contact/code/${contactResult.hash}/download-complete-bulk-task/${taskId}`,
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;
      link.setAttribute(`download`, `${contactName}-multiple-invoices.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (err) {
      console.error(err);
      toast.error(`Could not download bulk invoices`);
    }

    setDownloadBulkLoading(false);
  }

  if (isLoading || !contactResult) {
    return <LoadingFallback />;
  }

  const sections = contactResult.statement.currencySections;

  if (!sections.length) {
    // No invoices
    return (
      <div className={ classNames(card, `px-5 py-4 text-sm text-gray-500`) }>
        { `No invoices found` }
      </div>
    );
  }

  const section = sections.find(s => s.currency === currency) ?? sections[0];
  const isOutstanding = type === `outstanding`;
  const allLines = sections.flatMap(cs => [...cs.lines, ...cs.paidLines]);

  function onCurrencyChange(next: string) {
    setCurrency(next);
    setSelectedInvoiceIds([]);
    setPayPick(null);
  }

  function onToggle(line: IStatementLine) {
    const next = selectedInvoiceIds.includes(line.invoiceId)
      ? selectedInvoiceIds.filter(id => id !== line.invoiceId)
      : [...selectedInvoiceIds, line.invoiceId];

    setSelectedInvoiceIds(next);
    setPayPick(next.length ? `selected` : null);
  }

  // Balances and pay options for the rail, in the selected currency
  const invoices = section.lines.filter(l => l.type === StatementLineType.Invoice);
  const split = getStatementSplitData(section);
  const dueNowInvoices = split.before.lines.filter(l => l.type === StatementLineType.Invoice);
  const overdueInvoices = invoices.filter(l => l.debtAgeInDays > 0);
  const selectedInvoices = invoices.filter(l => selectedInvoiceIds.includes(l.invoiceId));
  const showSplit = !!contactResult.showSplitData;
  const dueNowText = dueNowDescription(section.splitDayIndex);

  const payOptions: { key: PayOption; label: string; sub: string; lines: IStatementLine[]; disabled: boolean }[] = [
    ...(showSplit ? [{ key: `due` as const, label: `Due now`, sub: dueNowText, lines: dueNowInvoices }] : []),
    { key: `overdue` as const, label: `Overdue`, sub: `Past their due date`, lines: overdueInvoices },
    { key: `all` as const, label: `All outstanding`, sub: `Includes not yet due`, lines: invoices },
    {
      key: `selected` as const,
      label: `Selected invoices`,
      sub: selectedInvoices.length ? `${selectedInvoices.length} ticked below` : `Tick invoices below`,
      lines: selectedInvoices,
    },
  ].map(o => ({ ...o, disabled: o.key === `selected` ? !o.lines.length : sumOutstanding(o.lines) <= 0 }));

  const preferred = payPick ?? (showSplit ? `due` : `overdue`);
  const picked = payOptions.find(o => o.key === preferred && !o.disabled) ?? payOptions.find(o => !o.disabled) ?? payOptions[0];

  const directDebitLabel = contactResult.stripeDirectDebitByCurrency?.[section.currency.toUpperCase()];
  const method: `card` | `dd` = directDebitLabel ? paymentMethod : `card`;
  const canPay = !!(contactResult.stripeEnabled && contactResult.stripeConnectAccount && contactResult.statement.hasOutstandingBalance && invoices.length);
  const pickedAmount = formatAmount(sumOutstanding(picked.lines), section.currency);
  let payLabel = `Pay ${pickedAmount}`;

  if (directDebitLabel) {
    payLabel += method === `dd` ? ` by ${directDebitLabel}` : ` by card`;
  }

  const aged = contactResult.agedBreakdowns?.[section.currency] ?? [];
  const agedTotal = aged.reduce((acc, a) => acc + a.totalCents, 0);

  const money = (cents: number) => formatAmount(cents, section.currency);

  const statementCard = (
    <StatementSection
      type={ type }
      currencySection={ section }
      currencies={ sections.map(s => s.currency) }
      onCurrencyChange={ onCurrencyChange }
      statementPeriod={ contactResult.statement.period }
      onDownload={ downloadStatement }
      loading={ downloadStatementLoading }
      organisationTimezone={ contactResult.statement.organisationTimezone }
      childContacts={ contactResult.childContacts }
      onDownloadInvoice={ onDownloadInvoice }
      downloadingInvoices={ downloadingInvoices }
      showSplitBalances={ showSplit }
      columnSettings={ contactResult.columnSettings }
      selectedInvoiceIds={ selectedInvoiceIds }
      onToggle={ onToggle }
      allowedDownloadActivity={ contactResult.allowedDownloadActivity }
      invoiceLinksUseProxy={ contactResult.invoiceLinksUseProxy }
      organisationId={ contactResult.organisationId }
      contactHash={ contactResult.hash }
      contactName={ contactResult.statement.contact.name }
      refetchContact={ refetch }
    />
  );

  return (
    <div className={ `flex flex-col gap-4` }>
      { paymentComplete && (
        <Banner
          tone={ `green` }
          icon={ CheckCircleIcon }
        >
          { /* Bank debits take days to clear, so only a card payment is known to have gone through */ }
          <span className={ `flex-1` }>{ method === `card` ? `Payment successful!` : `Payment Submitted - please wait for updates` }</span>
          { /*  Work around for race condition */ }
          <a
            href={ window.location.href }
            onClick={ () => window.location.reload() }
            className={ `text-[0.8125rem] text-green-700 underline` }
          >
            { `Reload` }
          </a>
        </Banner>
      ) }

      { contactResult.bulkDownloadTasks?.map(task => {
        const completed = task.status === BulkDownloadInvoiceTaskStatus.COMPLETED;
        const invoiceNumbers = task.backgroundTasks.map(t => allLines.find(l => l.invoiceId === t.invoiceId)?.invoiceNumber || `[Paid Invoice]`);
        const completeCount = task.backgroundTasks.filter(t => t.status === BackgroundTaskStatus.COMPLETE).length;

        return (
          <Banner
            key={ task.id }
            tone={ completed ? `green` : `amber` }
            icon={ completed ? CheckCircleIcon : ClockIcon }
          >
            <span className={ `flex flex-[1_1_15rem] flex-col gap-0.5` }>
              <span>
                { completed ? `Your bulk invoice download is ready` : `Your bulk invoice download is being processed` }
                { ` for ${task.backgroundTasks.length} invoices.` }
                { !completed && ` (${completeCount} of ${task.backgroundTasks.length})` }
              </span>
              <span className={ `text-xs` }>{ `(${invoiceNumbers.join(`, `)})` }</span>
            </span>
            { completed && (
              <button
                type={ `button` }
                className={ `inline-flex h-[2.125rem] shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-green-300 bg-white px-3 text-[0.8125rem] font-semibold text-green-700 disabled:cursor-wait disabled:opacity-50` }
                disabled={ downloadBulkLoading }
                onClick={ () => onDownloadBulkJob(task.id) }
              >
                <ArrowDownTrayIcon className={ `h-[0.9375rem] w-[0.9375rem]` } />
                { `Download` }
              </button>
            ) }
          </Banner>
        );
      }) }

      { !isOutstanding && statementCard }

      { isOutstanding && (
        // Balance rail on the right, moves above the statement when there is no room
        <div className={ `flex flex-row-reverse flex-wrap items-start gap-5` }>
          <div className={ `flex min-w-0 flex-[1_1_17.5rem] flex-col gap-4` }>
            <div className={ card }>
              <div className={ `flex flex-col gap-1 px-5 pb-3 pt-5` }>
                <span className={ `text-[0.8125rem] text-gray-500` }>{ `Total outstanding` }</span>
                <span className={ `text-[2rem] font-semibold leading-tight tracking-[-0.02em] tabular-nums text-gray-900` }>
                  { money(section.totalOutstandingGrossInCents) }
                </span>
              </div>
              <div className={ `flex flex-col gap-2 px-5 pb-4 text-sm tabular-nums` }>
                { showSplit ? (
                  <>
                    <SummaryRow
                      dot={ `bg-amber-500` }
                      label={ `Due now` }
                      sub={ dueNowText }
                      value={ money(split.before.totalOutstandingGrossInCents) }
                    />
                    <SummaryRow
                      dot={ `bg-paidniceGreen-300` }
                      label={ `Not yet due` }
                      value={ money(split.after.totalOutstandingGrossInCents) }
                    />
                  </>
                ) : (
                  <>
                    <SummaryRow
                      dot={ `bg-paidniceRed-600` }
                      label={ `Overdue` }
                      value={ money(section.totalOverdueGrossInCents) }
                      red
                    />
                    <SummaryRow
                      dot={ `bg-paidniceGreen-300` }
                      label={ `Not yet due` }
                      value={ money(section.totalOutstandingGrossInCents - section.totalOverdueGrossInCents) }
                    />
                  </>
                ) }
                <div className={ `flex flex-col gap-2 border-t border-dashed border-gray-200 pt-2` }>
                  { showSplit && (
                    <SummaryRow
                      dot={ `bg-paidniceRed-600` }
                      label={ `Overdue` }
                      value={ money(section.totalOverdueGrossInCents) }
                      red
                    />
                  ) }
                  <div className={ `flex justify-between gap-2.5` }>
                    <span className={ `text-gray-600` }>{ `Balance after credits` }</span>
                    <span className={ `font-semibold text-gray-900` }>{ money(section.totalOutstandingInCents) }</span>
                  </div>
                </div>
              </div>

              { canPay && (
                <div className={ `flex flex-col gap-2.5 border-t border-gray-100 px-5 py-4` }>
                  <span
                    id={ `pay-option-label` }
                    className={ `text-[0.8125rem] font-semibold text-gray-700` }
                  >
                    { `What would you like to pay?` }
                  </span>
                  <div
                    role={ `radiogroup` }
                    aria-labelledby={ `pay-option-label` }
                    className={ `flex flex-col divide-y divide-gray-100 overflow-hidden rounded-[0.625rem] border border-gray-200` }
                  >
                    { payOptions.map(o => {
                      const on = o.key === picked.key;

                      return (
                        <button
                          key={ o.key }
                          type={ `button` }
                          role={ `radio` }
                          aria-checked={ on }
                          disabled={ o.disabled }
                          onClick={ () => setPayPick(o.key) }
                          className={ classNames(
                            `flex items-start gap-2.5 px-3 py-2.5 text-left text-sm`,
                            on ? `bg-paidniceGreen-50` : `bg-white`,
                            o.disabled ? `cursor-default text-gray-400` : `cursor-pointer text-gray-900`,
                          ) }
                        >
                          <span className={ classNames(
                            `mt-0.5 h-4 w-4 shrink-0 rounded-full`,
                            on ? `border-[5px] border-paidniceGreen-600` : `border-[1.5px]`,
                            !on && (o.disabled ? `border-gray-200` : `border-gray-400`),
                          ) } />
                          <span className={ `flex min-w-0 flex-1 flex-col gap-0.5` }>
                            <span className={ `flex items-baseline justify-between gap-2` }>
                              <span className={ `truncate font-medium` }>{ o.label }</span>
                              { !(o.key === `selected` && o.disabled) && (
                                <span className={ `whitespace-nowrap font-semibold tabular-nums` }>{ money(sumOutstanding(o.lines)) }</span>
                              ) }
                            </span>
                            <span className={ `truncate text-xs text-gray-500` }>{ o.sub }</span>
                          </span>
                        </button>
                      );
                    }) }
                  </div>
                  { directDebitLabel && (
                    <Segmented<`card` | `dd`>
                      options={ [{ value: `card`, label: `Card` }, { value: `dd`, label: directDebitLabel }] }
                      value={ method }
                      onChange={ setPaymentMethod }
                    />
                  ) }
                  <button
                    type={ `button` }
                    className={ classNames(primaryPayButton, `disabled:cursor-not-allowed disabled:opacity-50`) }
                    disabled={ picked.disabled || !!invoiceIdsToPay }
                    onClick={ () => setInvoiceIdsToPay(picked.lines.map(l => l.invoiceId)) }
                  >
                    { payLabel }
                  </button>
                </div>
              ) }
            </div>

            <SavedPaymentMethods contact={ contactResult } />

            { contactResult.footer && (
              <div className={ classNames(card, `flex flex-col gap-2 px-5 py-3.5`) }>
                <span className={ `flex items-center gap-2 text-sm font-semibold text-gray-900` }>
                  <InformationCircleIcon className={ `h-[1.125rem] w-[1.125rem] text-paidniceGreen-600` } />
                  { `More information` }
                </span>
                <div className={ `whitespace-pre-wrap break-words text-[0.8125rem] leading-relaxed text-gray-600` }>
                  { contactResult.footer }
                </div>
              </div>
            ) }

            { contactResult.showSMSOptIn && (
              <div className={ classNames(card, `px-5 py-3.5`) }>
                <Toggle
                  checked={ !contactResult.smsOptOut }
                  onChange={ onSmsOptOut }
                  label={ `SMS Communications` }
                  description={ `I consent to receiving SMS communications from Paidnice on behalf of ${contactResult.senderDetail?.customerFacingCompanyName}` }
                  helpIcon={ `Other businesses using Paidnice may also send you SMS communications. Change your preferences on the relevant portal page.` }
                  loading={ smsOptOutLoading }
                  disabled={ smsOptOutLoading || !session?.id }
                />
              </div>
            ) }
          </div>

          <div className={ `flex min-w-0 flex-[999_1_37.5rem] flex-col gap-4` }>
            { statementCard }

            { aged.length > 0 && (
              <div className={ classNames(card, `flex flex-col gap-3 px-[clamp(1rem,3cqi,1.5rem)] py-4`) }>
                <div className={ `flex flex-wrap items-baseline justify-between gap-2.5` }>
                  <span className={ `text-sm font-semibold text-gray-900` }>{ `Aged debt breakdown` }</span>
                  <span className={ `text-[0.8125rem] text-gray-500` }>{ `${section.currency} · by days overdue` }</span>
                </div>
                <div className={ `flex h-2.5 gap-0.5 overflow-hidden rounded-[0.3125rem] bg-gray-100` }>
                  { aged.map((a, i) => a.totalCents > 0 && (
                    <span
                      key={ a.title }
                      className={ agedColors[i] ?? agedColors[agedColors.length - 1] }
                      style={ { width: `${(a.totalCents / agedTotal) * 100}%` } }
                    />
                  )) }
                </div>
                <div className={ `grid grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] gap-3` }>
                  { aged.map((a, i) => (
                    <div
                      key={ a.title }
                      className={ `flex flex-col gap-0.5` }
                    >
                      <span className={ `flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-500` }>
                        <span className={ classNames(`h-2 w-2 rounded-sm`, agedColors[i] ?? agedColors[agedColors.length - 1]) } />
                        { a.title }
                      </span>
                      <span className={ classNames(
                        `text-[0.9375rem] font-semibold tabular-nums`,
                        // Dont make the first bracket red
                        a.totalCents > 0 && i > 0 ? `text-paidniceRed-700` : `text-gray-900`,
                      ) }>
                        { a.total }
                      </span>
                    </div>
                  )) }
                </div>
              </div>
            ) }
          </div>
        </div>
      ) }

      { invoiceIdsToPay && (
        <StripeCheckout
          mode={ `statement` }
          code={ contactResult.hash }
          connectedAccountId={ contactResult.stripeConnectAccount }
          onClose={ () => setInvoiceIdsToPay(null) }
          onPaid={ () => {
            setInvoiceIdsToPay(null);
            setSelectedInvoiceIds([]);
            setPaymentComplete(true);
          } }
          invoiceIds={ invoiceIdsToPay }
          paymentMethod={ method }
        />
      ) }
    </div>
  );
}
